/**
 * Department News Feed — contracts + controlled vocabulary.
 *
 * THE single authored copy of the news category vocabulary. The backend
 * classifier (apps/backend/services/news/newsVocab.js) is CommonJS and keeps
 * a marked MIRROR — same convention as noteVocab.ts. Change here first, then
 * the mirror, then re-run `scripts/news-ingest.js --reclassify`.
 *
 * Line: INTEL. Everything here is public web news about California agencies
 * plus CRM-derived signals (staff changes, new BCPs, new solicitations).
 */

export type NewsCategory =
  | 'leadership'      // appointments, departures, CIO/CISO/director moves
  | 'staffing'        // hiring, layoffs, reorganizations, workforce
  | 'budget'          // budget, BCPs, LAO/DOF, funding, appropriations
  | 'procurement'     // RFPs, solicitations, contract awards, vendors
  | 'cybersecurity'   // breaches, incidents, security audits, ransomware
  | 'technology'      // IT projects, modernization, AI, data, systems
  | 'legislation'     // bills, executive orders, regulations
  | 'audit'           // State Auditor / oversight findings
  | 'operations'      // general agency news, programs, services
  | 'crm';            // TekStak-derived signals (contact changes)

/** [value, label, hint] for the category chips/filters — display order. */
export const NEWS_CATEGORY_OPTIONS: ReadonlyArray<readonly [NewsCategory, string, string]> = [
  ['leadership', 'Leadership', 'Appointments, departures, CIO/CISO/director moves'],
  ['staffing', 'Staffing', 'Hiring, layoffs, reorganizations'],
  ['budget', 'Budget', 'Budget change proposals, LAO/DOF, funding'],
  ['procurement', 'Procurement', 'Solicitations, awards, vendor news'],
  ['cybersecurity', 'Cybersecurity', 'Breaches, incidents, security findings'],
  ['technology', 'Technology', 'IT projects, modernization, AI, data'],
  ['legislation', 'Legislation', 'Bills, executive orders, regulations'],
  ['audit', 'Audit', 'State Auditor and oversight reports'],
  ['operations', 'Operations', 'General agency programs and services'],
  ['crm', 'CRM signal', 'Derived from your org charts and contacts'],
] as const;

export const NEWS_CATEGORY_LABEL: Readonly<Record<NewsCategory, string>> = Object.fromEntries(
  NEWS_CATEGORY_OPTIONS.map(([k, label]) => [k, label])
) as Record<NewsCategory, string>;

/** Lane — Ryan's three categories (2026-09-08), stored on every item. */
export type NewsLane = 'state' | 'department' | 'industry';

/** Feed scope — who the feed is for. mine/all are department-lane views. */
export type NewsScope = 'mine' | 'all' | 'state' | 'industry';

export const NEWS_SCOPE_OPTIONS: ReadonlyArray<readonly [NewsScope, string, string]> = [
  ['mine', 'My accounts', 'Departments on your account lists or assigned to you'],
  ['all', 'All agencies', 'Every department-tagged item'],
  ['state', 'Statewide', 'Capitol, budget and statewide IT with no single department'],
  ['industry', 'Industry', 'Cybersecurity and technology beyond California — research, incidents, vendors'],
] as const;

export type NewsSourceKind = 'rss' | 'gnews' | 'internal' | 'assistant';

export type NewsSourceTier =
  | 'ca_it_trade' | 'ca_politics' | 'ca_newsroom' | 'agency_newsroom'
  | 'national_govtech' | 'security' | 'industry' | 'local_gov' | 'sector' | 'internal' | 'assistant';

export interface NewsDepartmentTag {
  department_id: number;
  acronym: string | null;
  name: string | null;
  match_kind: 'query' | 'source' | 'name' | 'acronym' | 'contact' | 'internal' | 'manual';
  confidence: number;
}

export interface NewsContactTag {
  contact_id: string;
  name: string;
  title: string | null;
  department_id: number | null;
  confidence: number;
}

export interface NewsItem {
  id: number;
  source_key: string;
  source_name: string;
  source_tier: NewsSourceTier;
  title: string;
  summary: string | null;
  url: string | null;
  publisher: string | null;
  published_at: string | null;
  fetched_at: string;
  categories: NewsCategory[];
  importance: number;
  departments: NewsDepartmentTag[];
  contacts: NewsContactTag[];
  /** Present for internal signals: { kind, id, ... } pointing at the CRM/BCP/solicitation row. */
  internal_ref: Record<string, unknown> | null;
  lane: NewsLane;
  /** Who filed it when it came through submit_news_item (client slug or email); null for polled feeds. */
  submitted_by: string | null;
  submitted_via: string | null;
  /** FTS rank when the request carried q. */
  rank?: number;
  /**
   * Enrichment (migration 1813100000000). The publisher's lead image URL —
   * never rehosted — or null: absent for Google News interstitials (which
   * cannot resolve), for internal signals (which have no article), and for
   * the third of publishers that ship no og:image. The read path has carried
   * these since the enricher landed; the contract did not, which is why no
   * client drew them.
   */
  image_url: string | null;
  /** True when a stored article body exists; the text itself is fetched on demand. */
  has_body: boolean;
}

export interface NewsFeedFilters {
  scope: NewsScope;
  department_ids: number[];
  categories: NewsCategory[];
  q: string | null;
  since: string | null;
}

/** Body of POST /api/v1/department-news/items (submit_news_item). */
export interface NewsSubmission {
  title: string;
  url?: string | null;
  summary?: string | null;
  publisher?: string | null;
  published_at?: string | null;
  categories?: NewsCategory[] | string;
  lane?: NewsLane;
  /** Department acronyms/ids to tag explicitly (text matching still runs). */
  departments?: string[] | string;
}

export interface NewsSubmitResponse {
  item: NewsItem;
  created: boolean;
  /** True when a feed already carried the story — the submission merged into it. */
  merged: boolean;
}

export interface NewsFeedResponse {
  items: NewsItem[];
  filters: NewsFeedFilters;
  /** Pass back as ?before= to page older. */
  next_before: string | null;
  counts: { total_window: number; by_category: Partial<Record<NewsCategory, number>> };
  /** When the caller is on scope=mine but owns no departments. */
  empty_reason?: 'no_accounts' | null;
}

/** GET /api/v1/department-news/contacts/:id — one CRM contact's stories (their profile's News tab). */
export interface NewsContactFeedResponse {
  contact: {
    contact_id: string;
    name: string;
    title: string | null;
    email: string | null;
    department_id: number | null;
    account_name: string | null;
    /** Set when the contact was soft-deleted; their stories are still theirs. */
    deleted_at: string | null;
  };
  items: NewsItem[];
  next_before: string | null;
  /** Every visible story ever tagged to them, regardless of the window. */
  counts: { total: number };
  filters: { since: string; categories: NewsCategory[] };
}

export interface NewsSourceHealth {
  key: string;
  name: string;
  kind: NewsSourceKind;
  tier: NewsSourceTier;
  enabled: boolean;
  last_polled_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
  items_total: number;
  items_7d: number;
}
